import type { NotificationListItem } from './notification-api';
import type { NotificationFile } from './types';
import { getApiUrl } from '@/js/utils/getApiUrl';

export type NotificationCardItem = {
  id: number;
  title: string;
  description: string;
  date: string;
  status: string;
  statusCode: string;
  isNew: boolean;
};

export function formatNotificationDate(iso: string): string {
  if (!iso) return '';

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;

  const datePart = date.toLocaleDateString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  const timePart = date.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return `${datePart} / ${timePart}`;
}

export function normalizeNotificationStatusCode(status?: string | null): string {
  const normalized = (status ?? '').trim().toLowerCase();

  if (normalized === 'new' || normalized === 'новый' || normalized === 'новое') {
    return 'new';
  }

  if (normalized === 'reply' || normalized === 'поступил ответ' || normalized === 'получен ответ') {
    return 'reply';
  }

  if (normalized === 'read' || normalized === 'прочитано') {
    return 'read';
  }

  return normalized;
}

export function getNotificationStatusLabel(status?: string | null): string {
  switch (normalizeNotificationStatusCode(status)) {
    case 'new':
      return 'Новое';
    case 'reply':
    case 'read':
      return 'Прочитано';
    default:
      return 'Прочитано';
  }
}

export function getManagerNotificationStatusLabel(status?: string | null): string {
  switch (normalizeNotificationStatusCode(status)) {
    case 'new':
      return 'Доставлено';
    case 'reply':
      return 'Получен ответ';
    case 'read':
      return 'Прочитано';
    default:
      return 'Прочитано';
  }
}

export function getManagerNotificationStatusModifier(status?: string | null): string {
  switch (normalizeNotificationStatusCode(status)) {
    case 'new':
      return 'new';
    case 'reply':
      return 'reply';
    case 'read':
    default:
      return 'read';
  }
}

export function countManagerUnreadNotifications(
  messages: Array<{ status?: string | null }>,
): number {
  return messages.filter(
    (message) => normalizeNotificationStatusCode(message.status) === 'reply',
  ).length;
}

export function isUnreadNotificationStatus(status?: string | null): boolean {
  return normalizeNotificationStatusCode(status) === 'new';
}

export function isReplyNotificationStatus(status?: string | null): boolean {
  return normalizeNotificationStatusCode(status) === 'reply';
}

function resolveNotificationFileUrl(url: string): string {
  const normalized = url.trim();

  if (!normalized) {
    return '#';
  }

  if (/^https?:\/\//i.test(normalized)) {
    return normalized;
  }

  if (normalized.startsWith('/')) {
    return `${window.location.origin}${normalized}`;
  }

  return getApiUrl(normalized);
}

function normalizeNotificationFile(value: unknown): NotificationFile | null {
  if (typeof value === 'number' || (typeof value === 'string' && /^\d+$/.test(value))) {
    return null;
  }

  if (!value || typeof value !== 'object') {
    return null;
  }

  const file = value as Record<string, unknown>;
  const name = String(
    file.name ?? file.fileName ?? file.originalName ?? file.ORIGINAL_NAME ?? '',
  ).trim();
  const rawUrl = String(file.url ?? file.src ?? file.SRC ?? file.path ?? '').trim();

  if (!name && !rawUrl) {
    return null;
  }

  return {
    name: name || 'Файл',
    url: resolveNotificationFileUrl(rawUrl),
    size: file.size != null ? String(file.size) : undefined,
    type: file.type != null ? String(file.type) : undefined,
  };
}

export function normalizeNotificationFiles(files: unknown): NotificationFile[] {
  if (!files) {
    return [];
  }

  const list = Array.isArray(files) ? files : Object.values(files as Record<string, unknown>);

  return list.map(normalizeNotificationFile).filter((file): file is NotificationFile => file !== null);
}

export function mapNotificationToCard(item: NotificationListItem): NotificationCardItem {
  const statusCode = normalizeNotificationStatusCode(item.status);

  return {
    id: item.id,
    title: item.subject,
    description: item.text,
    date: formatNotificationDate(item.createdAt),
    status: getNotificationStatusLabel(statusCode),
    statusCode,
    isNew: isUnreadNotificationStatus(statusCode),
  };
}
